import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { CanGoOutAloneConditionInput } from '../../../lib/types/models/can-go-out-alone-condition/can-go-out-alone-condition'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<CanGoOutAloneConditionInput> = z
  .object({
    minAge: createNumberSchema('age min', { isInteger: true }),
    maxAge: createNumberSchema('age max', { isInteger: true }),
    countries: createNumberArraySchema('pays', { isInteger: true, minimumNumberOfItems: 1 }),
    formulas: createNumberArraySchema('formules', { isInteger: true, minimumNumberOfItems: 1 })
  })
  .superRefine((data, ctx) => {
    if (data.minAge > data.maxAge) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "L'âge min ne peut être supérieur à l'âge max",
        path: ['minAge']
      })
    }
  })

export const CanGoOutAloneConditionForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<CanGoOutAloneConditionInput>) => {
  const form = useForm<CanGoOutAloneConditionInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  return (
    <Form form={form}>
      <form
        className={'flex flex-col gap-4'}
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
      >
        <Box className={'flex flex-col gap-4'}>
          <Typography variant={'body-medium'} color={tailwindColors.primary.DEFAULT}>
            Conditions de sortie seul
          </Typography>
          <FormField
            control={form.control}
            name={'minAge'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type={'number'}
                    label={'Âge min'}
                    min={0}
                    max={99}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'maxAge'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type={'number'}
                    label={'Âge max'}
                    min={1}
                    max={100}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'countries'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    label={'Pays'}
                    variant={'primary'}
                    options={
                      countries?.data.map((country) => ({
                        label: country.name,
                        value: country.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((countryId) => ({
                        label:
                          countries?.data.find((country) => country.id === countryId)?.name ?? '',
                        value: countryId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'formulas'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    label={'Formule(s)'}
                    variant={'primary'}
                    options={
                      formulas?.data.map((formula) => ({
                        label: getTranslatedName(formula),
                        value: formula.id
                      })) ?? []
                    }
                    searchable
                    selectedOptions={
                      field.value?.map((formulaId) => ({
                        label: getTranslatedName(
                          formulas?.data.find((formula) => formula.id === formulaId)
                        ),
                        value: formulaId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <ValidationButtons {...validationButtonProps} />
        </Box>
      </form>
    </Form>
  )
}
